import {
  faAngleDown,
  faFileContract,
  faFileInvoice,
  faFileInvoiceDollar,
  faUpload,
} from '@fortawesome/pro-regular-svg-icons'
import { faFileLines } from '@fortawesome/pro-regular-svg-icons/faFileLines'
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus'
import { Box, Button, Flex, Menu, Text } from '@mantine/core'
import { clsx } from 'clsx'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'

import { useAnalytics } from '@flecto/analytics'
import {
  useQueryGetAllBookingDocuments,
  useQueryGetAllBookingProducts,
} from '@flecto/core/saas'
import { useQueryGetUserBookingsSettings } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type CoreBookingStatus, type CoreBookingDocument } from '@flecto/types'
import { Icon, BoxCollapsible } from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import {
  useBookingActions,
  useModalBookingActionCreateContract,
  useModalBookingActionCreateQuote,
} from '../../../booking-actions'
import { useModalBookingActionCreateProforma } from '../../../booking-actions/hooks/useModalBookingActionCreateProforma'
import { useBookingStore } from '../../../booking-details/hooks/useBookingStore'
import { useModalAddDocument } from '../../hooks/useModalAddDocument'
import { useModalNoContractTemplate } from '../../hooks/useModalNoContractTemplate'
import { useModalViewDocument } from '../../hooks/useModalViewDocument'
import { DocumentBoxItem } from '../DocumentBoxItem/DocumentBoxItem'
import styles from './styles.module.css'

export const DocumentBox = ({
  bookingStatus,
}: {
  bookingStatus: CoreBookingStatus
}) => {
  const router = useRouter()
  const id = String(router.query?.id)
  const track = useAnalytics('segment')

  const { t: tBooking } = useTranslation('saas', { keyPrefix: 'booking' })

  const { data: bookingItems } = useQueryGetAllBookingProducts({
    bookingId: id,
  })

  const { data } = useQueryGetAllBookingDocuments({ bookingId: id })

  const { data: bookingSettings } = useQueryGetUserBookingsSettings()

  const { hasCustomer, noHasCustomerNotification } = useBookingActions({
    bookingId: id,
  })

  const { setDocuments } = useBookingStore()

  const noContractTemplate =
    (!bookingSettings?.contract_body ||
      bookingSettings?.contract_body === '<p></p>' ||
      bookingSettings?.contract_body === null ||
      bookingSettings?.contract_body === '\u003cp\u003e\u003c/p\u003e') &&
    !bookingSettings?.contract_title &&
    !bookingSettings?.documents_contract_include_customer_details &&
    !bookingSettings?.documents_contract_include_rental_details &&
    !bookingSettings?.documents_contract_include_signatures &&
    !bookingSettings?.documents_contract_include_links &&
    !bookingSettings?.documents_contract_include_terms_and_conditions &&
    !bookingSettings?.documents_contract_include_id_on_title &&
    !bookingSettings?.documents_contract_include_generated_date

  const files = data?.booking_document || []

  const countFiles = React.useMemo(() => {
    return files.length
  }, [files])
  const hasDoc = countFiles > 0
  const { isMobile, isTablet } = useMediaQuery()

  const { openModal: openModalEnable } = useModalAddDocument()
  const { openModal: openModalViewEnable } = useModalViewDocument()

  const { openModal: openModalCreateQuote } = useModalBookingActionCreateQuote({
    bookingId: id,
  })

  const { openModal: openModalCreateContract } =
    useModalBookingActionCreateContract({
      bookingId: id,
    })

  const { openModal: openModalNoContractTemplate } =
    useModalNoContractTemplate()

  const { openModal: openModalCreateProforma } =
    useModalBookingActionCreateProforma({
      bookingId: id,
    })

  useEffect(() => {
    if (data?.booking_document) {
      setDocuments(data.booking_document)
    }
  }, [data, setDocuments])

  return (
    <BoxCollapsible
      title={tBooking('booking_documents.title')}
      badge={countFiles}
      viewAll={hasDoc ? openModalViewEnable : undefined}
    >
      {!hasDoc ? (
        <Flex gap='md' wrap='nowrap' align='center' justify='space-between'>
          <Box>
            <Flex align='center' columnGap='md'>
              <Icon
                icon={faFileLines}
                size='sm'
                withCircleBackground
                style={{ flexShrink: 0 }}
              />
              <Text c='dimmed'>
                {tBooking('booking_documents.no_documents')}
              </Text>
            </Flex>
          </Box>
        </Flex>
      ) : (
        <Box>
          <Flex
            className={clsx({
              [styles.titleMobile]: isMobile,
              [styles.titleTablet]: !isMobile && isTablet,
            })}
            w={'100%'}
            justify={'center'}
            direction={'column'}
          >
            {files.slice(0, 5).map((document: CoreBookingDocument, index) => (
              <DocumentBoxItem key={index} id={id} document={document} />
            ))}
          </Flex>
        </Box>
      )}
      <Menu withinPortal>
        <Menu.Target>
          <Button
            size='sm'
            variant='outline'
            leftSection={<Icon icon={faPlus} />}
            fullWidth
            mt='sm'
            data-cy='add-item-action'
          >
            <Flex align='center' gap='xs'>
              <Box>{tBooking('booking_documents.new_document')}</Box>
              <Icon icon={faAngleDown} />
            </Flex>
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<Icon icon={faUpload} />}
            onClick={openModalEnable}
          >
            {tBooking('booking_documents.upload_documents')}
          </Menu.Item>
          <Menu.Item
            leftSection={<Icon icon={faFileInvoice} />}
            onClick={() => {
              if (!hasCustomer) {
                noHasCustomerNotification()
              } else {
                openModalCreateQuote()
                track('BOOKING_QUOTE__NEW', {
                  bookingID: id ?? '',
                  bookingStatus: bookingStatus ?? '',
                  clickedOn: 'document-box-button',
                })
              }
            }}
            disabled={bookingItems?.length <= 0}
          >
            {tBooking('booking_documents.new_quote')}
          </Menu.Item>

          <Menu.Item
            leftSection={<Icon icon={faFileContract} />}
            onClick={() => {
              if (!hasCustomer) {
                noHasCustomerNotification()
              } else if (noContractTemplate) {
                openModalNoContractTemplate()
              } else {
                openModalCreateContract()
                track('BOOKING_DOCUMENT__NEW', { type: 'contract' })
              }
            }}
            disabled={bookingItems?.length <= 0}
          >
            {tBooking('booking_documents.new_contract')}
          </Menu.Item>

          <Menu.Item
            leftSection={<Icon icon={faFileInvoiceDollar} />}
            onClick={() => {
              if (!hasCustomer) {
                noHasCustomerNotification()
              } else {
                openModalCreateProforma()
                track('BOOKING_DOCUMENT__NEW', { type: 'proforma' })
              }
            }}
            disabled={bookingItems?.length <= 0}
          >
            {tBooking('booking_documents.new_proforma')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </BoxCollapsible>
  )
}
